// SEO FAQs Data - Dynamic FAQ content for each service type
// Used for FAQPage Schema and rich snippets in Google Search

export const serviceFaqs = {
  'cctv-installation': [
    {
      question: 'How much does CCTV installation cost in {city}?',
      answer: 'CCTV installation costs in {city} typically range from $500 for a basic 4-camera system to $5,000+ for commercial-grade systems with advanced features. IQES provides free on-site estimates tailored to your specific security needs.'
    },
    {
      question: 'What type of security cameras are best for {city} businesses?',
      answer: 'For {city} businesses, we recommend IP cameras with 4K resolution, night vision, and weatherproof ratings (IP66 or higher). Brands like Axis, Hikvision, and Avigilon offer excellent reliability for Florida\'s climate.'
    },
    {
      question: 'Do you offer remote viewing for CCTV systems in {city}?',
      answer: 'Yes, all our CCTV installations in {city} include remote viewing setup. You can monitor your property 24/7 from your smartphone, tablet, or computer using secure mobile apps.'
    },
    {
      question: 'How long does CCTV installation take?',
      answer: 'A typical residential CCTV installation in {city} takes 4-8 hours. Commercial installations may take 1-3 days depending on the number of cameras and complexity of the system.'
    },
    {
      question: 'Is IQES licensed to install security cameras in {city}, Florida?',
      answer: 'Yes, IQES is a fully licensed and insured low voltage contractor in Florida with over 13 years of experience installing CCTV systems throughout {city} and {region}.'
    }
  ],
  'fiber-optic-installation': [
    {
      question: 'What is the cost of fiber optic installation in {city}?',
      answer: 'Fiber optic installation costs in {city} vary based on distance and complexity. Single-mode fiber runs typically cost $3-8 per foot installed, plus termination and testing. IQES provides detailed free quotes for all fiber projects.'
    },
    {
      question: 'Do you offer fiber splicing services in {city}?',
      answer: 'Yes, IQES provides professional fusion splicing services in {city} using Fujikura equipment. We perform OTDR testing on all splices to ensure optimal signal quality and industry-standard loss rates.'
    },
    {
      question: 'What types of fiber optic cable do you install?',
      answer: 'We install both single-mode and multi-mode fiber optic cables in {city}. This includes OS2 single-mode for long distances and OM3/OM4 multi-mode for data center and building backbone applications.'
    },
    {
      question: 'How long does fiber optic installation take in {city}?',
      answer: 'Fiber optic installation timelines depend on the project scope. A typical building backbone in {city} takes 2-5 days, while larger campus installations may take 1-2 weeks including testing and certification.'
    },
    {
      question: 'Does IQES provide fiber optic testing and certification?',
      answer: 'Yes, all fiber installations include comprehensive OTDR testing and Fluke certification. We provide detailed test reports for warranty documentation and compliance requirements.'
    }
  ],
  'structured-cabling': [
    {
      question: 'How much does structured cabling cost in {city}?',
      answer: 'Structured cabling in {city} typically costs $150-300 per drop for Cat6, including materials, labor, and testing. Cat6A installations range from $200-400 per drop. IQES provides free estimates for all cabling projects.'
    },
    {
      question: 'What is the difference between Cat6 and Cat6A cabling?',
      answer: 'Cat6 supports 10Gbps up to 55 meters, while Cat6A supports 10Gbps up to 100 meters with better shielding against crosstalk. For {city} businesses planning for future growth, we often recommend Cat6A.'
    },
    {
      question: 'Do you install patch panels and network racks in {city}?',
      answer: 'Yes, IQES provides complete structured cabling solutions in {city} including patch panel installation, network rack setup, cable management, and professional labeling for easy maintenance.'
    },
    {
      question: 'Is your cabling work certified and tested?',
      answer: 'All structured cabling installations by IQES are tested and certified using Fluke DSX CableAnalyzer equipment. We provide detailed certification reports and warranty documentation.'
    },
    {
      question: 'Can you run cables through existing walls in {city} buildings?',
      answer: 'Yes, our technicians are experienced in running cables through existing walls, ceilings, and conduits in {city} buildings. We minimize disruption and ensure clean, professional installations.'
    }
  ],
  'access-control': [
    {
      question: 'What types of access control systems do you install in {city}?',
      answer: 'IQES installs card reader systems, keypad entry, biometric readers (fingerprint/facial recognition), and mobile access control solutions in {city}. We work with leading brands like HID and LiftMaster.'
    },
    {
      question: 'How much does access control installation cost in {city}?',
      answer: 'Access control costs in {city} start around $1,500 for a single-door system with card readers. Multi-door commercial systems typically range from $3,000-15,000+ depending on features and scale.'
    },
    {
      question: 'Can access control integrate with my existing security cameras?',
      answer: 'Yes, we specialize in integrating access control with CCTV systems in {city}. This allows automatic camera snapshots during access events and unified security management.'
    },
    {
      question: 'Do you offer cloud-based access control in {city}?',
      answer: 'Yes, IQES installs cloud-managed access control systems that allow you to manage door access, view logs, and issue credentials remotely from any device in {city} or anywhere else.'
    },
    {
      question: 'How long does access control installation take?',
      answer: 'A single-door access control installation in {city} typically takes 2-4 hours. Multi-door commercial systems may take 1-3 days depending on wiring requirements and system complexity.'
    }
  ],
  'security-cameras': [
    {
      question: 'What resolution security cameras do you recommend for {city}?',
      answer: 'For most {city} applications, we recommend 4K (8MP) cameras for critical areas and 4MP cameras for general coverage. Higher resolution provides better detail for identification and evidence.'
    },
    {
      question: 'Do you install PTZ cameras in {city}?',
      answer: 'Yes, IQES installs PTZ (pan-tilt-zoom) cameras throughout {city}. These are ideal for large areas like parking lots, warehouses, and commercial properties requiring flexible coverage.'
    },
    {
      question: 'Can security cameras work at night in {city}?',
      answer: 'All cameras we install in {city} feature infrared (IR) night vision or low-light technology. Many offer color night vision with supplemental lighting for enhanced identification.'
    },
    {
      question: 'How much storage do I need for security camera footage?',
      answer: 'Storage needs depend on camera count, resolution, and retention period. A typical 8-camera 4K system in {city} requires 4-8TB for 30 days of footage. We help size your NVR appropriately.'
    },
    {
      question: 'Do you offer license plate recognition cameras?',
      answer: 'Yes, IQES installs LPR (license plate recognition) cameras in {city} for parking lots, gated communities, and commercial entrances. These provide automatic plate capture and searchable databases.'
    }
  ],
  'network-installation': [
    {
      question: 'What network equipment brands do you install in {city}?',
      answer: 'IQES installs enterprise-grade networking equipment from Cisco, Ubiquiti, Fortinet, and HPE in {city}. We match equipment to your performance requirements and budget.'
    },
    {
      question: 'Do you configure network security in {city}?',
      answer: 'Yes, all network installations in {city} include security configuration: VLANs, firewall rules, secure WiFi, and proper access controls. We follow best practices to protect your business.'
    },
    {
      question: 'Can you set up WiFi for my {city} business?',
      answer: 'Yes, IQES provides complete enterprise WiFi solutions in {city} including site surveys, access point installation, and seamless roaming configuration for optimal coverage.'
    },
    {
      question: 'How much does commercial network installation cost?',
      answer: 'Network installation costs in {city} depend on scale and equipment. Small office setups start around $2,000, while larger deployments with multiple switches and access points range from $5,000-25,000+.'
    },
    {
      question: 'Do you provide ongoing network support in {city}?',
      answer: 'Yes, IQES offers network monitoring and support services in {city}. We can provide maintenance contracts for ongoing management, updates, and troubleshooting assistance.'
    }
  ],
  'access-point-installation': [
    {
      question: 'How many WiFi access points does my {city} business need?',
      answer: 'The number of access points depends on building size and user density. A typical {city} office needs 1 AP per 1,500-2,500 sq ft. IQES provides free WiFi site surveys to determine optimal placement.'
    },
    {
      question: 'What WiFi access point brands do you install?',
      answer: 'IQES installs Ubiquiti, Cisco Meraki, and Aruba access points in {city}. We recommend Ubiquiti for cost-effective solutions and Cisco/Aruba for enterprise environments requiring advanced features.'
    },
    {
      question: 'Do you provide WiFi heat mapping in {city}?',
      answer: 'Yes, we perform professional WiFi site surveys and heat mapping in {city} to ensure optimal coverage. This identifies dead zones and determines ideal access point placement.'
    },
    {
      question: 'Can you set up guest WiFi separate from our business network?',
      answer: 'Yes, all enterprise WiFi installations include guest network configuration with proper isolation from your business network, bandwidth limits, and customizable captive portals.'
    },
    {
      question: 'How long does WiFi access point installation take?',
      answer: 'Access point installation in {city} typically takes 1-2 hours per unit including mounting, cabling, and configuration. A full office deployment usually completes in 1-2 days.'
    }
  ],
  'cat6-cabling': [
    {
      question: 'What is the maximum speed of Cat6 cable?',
      answer: 'Cat6 cable supports speeds up to 10Gbps for distances up to 55 meters and 1Gbps up to 100 meters. It\'s suitable for most {city} business applications and future-ready for network upgrades.'
    },
    {
      question: 'How much does Cat6 cabling cost per drop in {city}?',
      answer: 'Cat6 cabling in {city} typically costs $150-250 per drop including cable, termination, testing, and wall plates. Volume discounts are available for larger projects.'
    },
    {
      question: 'Should I choose Cat6 or Cat6A for my {city} office?',
      answer: 'Cat6 is sufficient for most {city} offices. Choose Cat6A if you need guaranteed 10Gbps over 100m, require better shielding, or are planning for high-density data center environments.'
    },
    {
      question: 'Is Cat6 cabling tested after installation?',
      answer: 'Yes, IQES tests and certifies all Cat6 installations using Fluke DSX equipment. Each cable is verified for proper termination, signal integrity, and performance standards.'
    },
    {
      question: 'Can Cat6 cable support PoE devices?',
      answer: 'Yes, Cat6 cable fully supports Power over Ethernet (PoE, PoE+, PoE++) devices like IP cameras, access points, and VoIP phones commonly used in {city} businesses.'
    }
  ],
  'cat6a-cabling': [
    {
      question: 'What are the benefits of Cat6A over Cat6?',
      answer: 'Cat6A provides 10Gbps speeds over the full 100-meter distance, better shielding against crosstalk, and supports PoE++ for high-power devices. Ideal for {city} data centers and future-proof installations.'
    },
    {
      question: 'How much does Cat6A installation cost in {city}?',
      answer: 'Cat6A cabling in {city} typically costs $200-350 per drop due to thicker cables and specialized termination. The investment is worthwhile for data centers and high-performance networks.'
    },
    {
      question: 'Is Cat6A necessary for my {city} business?',
      answer: 'Cat6A is recommended for {city} businesses with data centers, high-density computing, healthcare facilities requiring high bandwidth, or those planning 10Gbps network upgrades.'
    },
    {
      question: 'Does Cat6A require special patch panels?',
      answer: 'Yes, Cat6A installations require Cat6A-rated patch panels, jacks, and patch cables to maintain full performance specifications. IQES uses only certified Cat6A components.'
    },
    {
      question: 'Can Cat6A cables run alongside electrical cables?',
      answer: 'Cat6A shielded (STP) cables offer better immunity to electromagnetic interference and can run closer to electrical cables than unshielded options, making them ideal for {city} industrial environments.'
    }
  ],
  'site-survey': [
    {
      question: 'What is included in a site survey in {city}?',
      answer: 'IQES site surveys in {city} include WiFi heat mapping, cable pathway assessment, equipment recommendations, labor estimates, and a detailed report with photos and diagrams.'
    },
    {
      question: 'How long does a site survey take?',
      answer: 'Site survey duration in {city} depends on facility size. Small offices take 1-2 hours, while large commercial buildings or campuses may require 4-8 hours for comprehensive assessment.'
    },
    {
      question: 'Is the site survey free?',
      answer: 'IQES offers complimentary site surveys in {city} for projects meeting minimum scope requirements. Contact us to discuss your project and schedule a free assessment.'
    },
    {
      question: 'Do you provide WiFi coverage reports?',
      answer: 'Yes, wireless site surveys include detailed heat maps showing signal strength, channel utilization, and recommended access point placements to ensure complete coverage in your {city} facility.'
    },
    {
      question: 'Can you survey existing cabling infrastructure?',
      answer: 'Yes, we assess existing cabling in {city} buildings to determine condition, capacity, and upgrade requirements. This helps plan cost-effective network improvements.'
    }
  ]
};

// Function to generate FAQs with city/region replacement
export const generateFaqsForPage = (serviceSlug, cityName, regionName) => {
  const faqs = serviceFaqs[serviceSlug];
  if (!faqs) return [];
  
  return faqs.map(faq => ({
    question: faq.question.replace(/{city}/g, cityName).replace(/{region}/g, regionName),
    answer: faq.answer.replace(/{city}/g, cityName).replace(/{region}/g, regionName)
  }));
};

// Generate enhanced meta descriptions per service type
export const serviceMetaDescriptions = {
  'cctv-installation': 'Expert CCTV installation in {city}, FL. HD/4K security cameras, NVR setup, remote viewing. Licensed contractor with 13+ years experience. Free quotes: 786-981-3760',
  'fiber-optic-installation': 'Professional fiber optic installation in {city}, FL. Single & multi-mode fiber, fusion splicing, OTDR testing. Certified technicians. Free estimates: 786-981-3760',
  'structured-cabling': 'Structured cabling services in {city}, FL. Cat5e, Cat6, Cat6A installation. Fluke certified, patch panels, racks. Licensed contractor. Call 786-981-3760',
  'access-control': 'Access control installation in {city}, FL. Card readers, biometric, mobile access. HID certified installer. 13+ years experience. Free quotes: 786-981-3760',
  'security-cameras': 'Security camera installation in {city}, FL. 4K cameras, PTZ, LPR, night vision. Licensed & insured. Remote viewing setup. Call IQES: 786-981-3760',
  'network-installation': 'Network installation in {city}, FL. Cisco, Ubiquiti, Fortinet. Switches, routers, firewalls, VLANs. Enterprise solutions. Free quotes: 786-981-3760',
  'access-point-installation': 'WiFi access point installation in {city}, FL. Ubiquiti, Cisco Meraki, Aruba. Site surveys, heat mapping. Enterprise WiFi experts: 786-981-3760',
  'cat6-cabling': 'Cat6 cabling installation in {city}, FL. Up to 10Gbps speeds. Fluke certified, tested & labeled. Professional installation from $150/drop. Call 786-981-3760',
  'cat6a-cabling': 'Cat6A cabling in {city}, FL. 10Gbps full distance, shielded options. Data center grade installation. Certified technicians. Free estimates: 786-981-3760',
  'site-survey': 'Professional site surveys in {city}, FL. WiFi heat mapping, cable pathway assessment, project planning. Free consultations available. Call IQES: 786-981-3760'
};

export const generateMetaDescription = (serviceSlug, cityName) => {
  const template = serviceMetaDescriptions[serviceSlug];
  if (!template) return null;
  return template.replace(/{city}/g, cityName);
};

export default {
  serviceFaqs,
  generateFaqsForPage,
  serviceMetaDescriptions,
  generateMetaDescription
};
